<template lang="pug">
    layout.contentTransparent(fluid)

        // Title
        title-component(
            :title="$t('programs.list.title')"
            slot="title"
            type="h2"
        )

        template
            v-layout(row wrap justify-space-between)
                v-flex(xs8 sm6 md4 lg3)
                    div.c-form.mr-2

                        // Search field
                        v-text-field(
                            :label="$t('fields.search')"
                            v-model="search"
                            append-icon="search"
                            class="search-input"
                            outline
                        )

                v-flex(xs4 sm6 md4)
                    div.createButtonWrapper

                        // Create Program button
                        button-component.mx-0.my-0(
                            :action="createProgram"
                            color="action"
                            large
                        ) {{ isMobile ? $t('programs.list.createButtonMobile') : $t('programs.list.createButton') }}

            separator-component.overlay

            v-layout(row wrap)
                v-flex(xs12)

                    // Programs list (Mobile)
                    v-data-table.c-table(
                        v-if="isMobile"
                        :headers-length="8"
                        :items="items"
                        :loading="isLoading"
                        :pagination.sync="pagination"
                        :total-items="total"
                        :rowsPerPageItems="[10]"
                    )
                        template(
                            v-slot:items="props"
                        )
                            tr(@click="props.expanded = !props.expanded")
                                list-mobile(
                                    :item="props.item"
                                    :expanded="props.expanded"
                                    @removed="resetPagination"
                                )

                        template(slot="expand" slot-scope="props")
                            tr
                                td(colspan="6")
                                    expand-card(:item="props.item")

                        template(v-slot:pageText="props")

                    // Programs list (Desktop)
                    v-data-table.c-table(
                        v-if="!isMobile"
                        :headers="headers"
                        :items="items"
                        :loading="isLoading"
                        :pagination.sync="pagination"
                        :total-items="total"
                        :rowsPerPageItems="[10]"
                    )

                        template(slot="items" slot-scope="props")
                            list-item(:item="props.item" @removed="resetPagination")

        controls-mobile(
            v-if="isMobile"
            :sortHandler="handleMobileSort"
        )
</template>

<script>
    import Layout from '../../Layout/Layout';
    import ListItem from './ListItem';
    import ListMobile from './ListItemMobile';
    import ExpandCard from './ExpandCard';
    import ControlsMobile from './ControlsMobile';
    import programs from '~api/programs';
    import Button from '~components/Button';
    import Filter from '~components/Filter';
    import Separator from '~components/Separator';
    import Title from '~components/Title';

    export default {
        components: {
            Layout,
            ListItem,
            ListMobile,
            ExpandCard,
            ControlsMobile,
            TitleComponent: Title,
            ButtonComponent: Button,
            FilterComponent: Filter,
            SeparatorComponent: Separator
        },
        data() {
            return {
                headers: this.$t('programs.tableHeaders'),
                items: [],
                pagination: {},
                total: 0,
                rowsPerPage: [10],
                search: null,
                timeout: null,
                routeInitialized: false,
                isLoading: false
            };
        },
        computed: {
            // Check screen resolution
            isMobile() {
                return this.$vuetify.breakpoint.smAndDown;
            }
        },

        /**
         * Retrieve Businesses
         */
        async beforeRouteEnter(to, from, next) {
            // Fetch data from API.
            const { data } = await programs.list();

            next(vm => {
                vm.items = data.items;

                vm.total = data.meta && data.meta.total ? data.meta.total : 0;

                vm.pagination = {
                    page: data.meta && data.meta.page ? data.meta.page : 0,
                    rowsPerPage: data.meta && data.meta.limit ? data.meta.limit : 0
                };

                vm.$nextTick(() => {
                    vm.routeInitialized = true;
                });
            });
        },
        watch: {
            /**
             * Watch search and set pagination param on change
             */
            search(value) {
                clearTimeout(this.timeout);

                this.timeout = setTimeout(() => {
                    this.pagination = {
                        ...this.pagination,
                        search: value
                    };
                }, 500);
            },

            /**
             * Watch pagination and update Businesses data on change
             */
            pagination: {
                async handler(paginationData) {
                    if (!this.routeInitialized) {
                        return;
                    }

                    // Start progress bar.
                    this.isLoading = true;

                    // Collect params.
                    const params = {
                        page: paginationData.page,
                        sortBy: paginationData.sortBy,
                        orderBy: paginationData.descending ? 'desc' : 'asc'
                    };

                    if (paginationData.search && paginationData.search.length > 0) {
                        params.search = paginationData.search;
                    }

                    // Fetch data from API.
                    const { data } = await programs.list(params);

                    this.items = data.items;
                    this.total = data.meta && data.meta.total ? data.meta.total : 0;

                    // Stop progress bar.
                    this.isLoading = false;
                },
                deep: true
            }
        },
        methods: {
            /**
             * Mobile sorting handler
             */
            handleMobileSort(name, e) {
                this.pagination.sortBy = name;
                this.pagination.descending = e;
                this.resetPagination();
            },

            /**
             * Reset pagination.
             */
            resetPagination() {
                this.pagination = { ...this.pagination };
            },

            /**
             * Redirect to Business create page
             */
            createProgram() {
                this.$router.push({ name: 'dashboard.programs.create' });
            },

            /**
             * Get filter param on change role filter
             * @param {string} e
             */
            filterRoleCallback(e) {
                this.filters.role = e;
                this.resetPagination();
            },

            /**
             * Change sort items
             * @param columnhandleMobileSort
             */
            changeSort(column) {
                if (this.pagination.sortBy === column) {
                    this.pagination.descending = !this.pagination.descending;
                } else {
                    this.pagination.sortBy = column;
                    this.pagination.descending = false;
                }
            }
        }
    };
</script>

<style lang="stylus" scoped>
    @import '../../../../assets/style/_variables.styl'

    .createButtonWrapper
        display: flex
        justify-content: flex-end

    >>> .actions
        min-width: 90px

    @media screen and (max-width: $grid-breakpoints.sm - 1)
        .createButtonWrapper
            .btn
                width: 100%
</style>
